import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../gyzmo-commons/dao/db/base/db.dao.base";
import { IdWordingDbDao } from "../../../gyzmo-commons/dao/db/idWording.db.dao";
import { isNullOrEmpty } from "../../../gyzmo-commons/helpers/null.helper";
import { AppSqlProvider } from "../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";
import { ThirdParty } from "../../models/thirdParty.model";
import { AddressDbDao } from "./address.db.dao";
import { ContactDetailsDbDao } from "./contactDetails.db.dao";
import { DriverInfoDbDao } from "./driverInfo.db.dao";

@Injectable({
    providedIn: "root",
})
export class ThirdPartyDbDao extends DbDaoBase<ThirdParty> {
    constructor(logger: LoggerService,
                private sqlProvider: AppSqlProvider,
                private addressDbDao: AddressDbDao,
                private contactDetailsDbDao: ContactDetailsDbDao,
                private idWordingDbDao: IdWordingDbDao,
                private driverInfoDbDao: DriverInfoDbDao) {
        super(logger);
    }

    public async createIndexes(): Promise<void> {
        // Ajouter ici la création d'index si nécessaire
    }

    public createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ ThirdParty.TABLENAME } (
            id TEXT PRIMARY KEY,
            civility TEXT,
            firstName TEXT,
            lastName TEXT,
            isActive INT,
            mainAddress TEXT,
            driverInformation TEXT,
            mail TEXT,
            mobile TEXT,
            phone TEXT,
            code TEXT,
            company TEXT
        );`;
        const params: any[] = [];
        return this.sqlProvider.query(query, params)
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public delete(id: string): Promise<any> {
        const query = `DELETE FROM ${ ThirdParty.TABLENAME } WHERE id = ?;`;
        const params = [id];
        return this.sqlProvider.query(query, params);
    }

    public deleteAll(): Promise<any> {
        const query = `DELETE FROM ${ ThirdParty.TABLENAME };`;
        const params: any[] = [];
        return this.sqlProvider.query(query, params);
    }

    public get(id: string, hydrate: boolean): Promise<ThirdParty> {
        if (isNullOrEmpty(id)) {
            return Promise.resolve(null);
        }
        const query = `SELECT * FROM ${ ThirdParty.TABLENAME } WHERE id = ?;`;
        const params = [id];
        return this.sqlProvider.query(query, params)
            .then(data => {
                if (data.rows.length <= 0) {
                    return null;
                }
                let thirdParty: ThirdParty = this.rowToModel(data.rows[0]);
                const hydratationPromises = [];
                if (hydrate) {
                    hydratationPromises.push(this.idWordingDbDao.get(thirdParty.company.id, hydrate)
                        .then(value => {
                            thirdParty.company = value;
                        }));
                    hydratationPromises.push(this.addressDbDao.get(thirdParty.mainAddress.id, hydrate)
                        .then(value => {
                            thirdParty.mainAddress = value;
                        }));
                    hydratationPromises.push(this.driverInfoDbDao.get(thirdParty.driverInformation.id, hydrate)
                        .then(value => {
                            thirdParty.driverInformation = value;
                        }));
                    hydratationPromises.push(this.contactDetailsDbDao.get(thirdParty.mail.id, hydrate)
                        .then(value => {
                            thirdParty.mail = value;
                        }));
                    hydratationPromises.push(this.contactDetailsDbDao.get(thirdParty.mobile.id, hydrate)
                        .then(value => {
                            thirdParty.mobile = value;
                        }));
                    hydratationPromises.push(this.contactDetailsDbDao.get(thirdParty.phone.id, hydrate)
                        .then(value => {
                            thirdParty.phone = value;
                        }));
                }
                return Promise.all(hydratationPromises)
                    .then(() => {
                        return thirdParty;
                    });
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return ThirdParty.TABLENAME;
    }

    protected rowToModel(row: any): ThirdParty {
        const thirdParty = new ThirdParty();
        thirdParty.id = row.id;
        thirdParty.firstName = row.firstName;
        thirdParty.lastName = row.lastName;
        thirdParty.isActive = row.isActive;
        thirdParty.mainAddress.id = row.mainAddress;
        thirdParty.driverInformation.id = row.driverInformation;
        thirdParty.mail.id = row.mail;
        thirdParty.mobile.id = row.mobile;
        thirdParty.phone.id = row.phone;
        thirdParty.company.id = row.company;
        thirdParty.code = row.code;
        thirdParty.civility = JSON.parse(row.civility);
        return thirdParty;
    }

    public save(thirdParty: ThirdParty): Promise<ThirdParty> {
        const promises = [];
        if (thirdParty.company) {
            promises.push(this.idWordingDbDao.save(thirdParty.company));
        }
        if (thirdParty.driverInformation) {
            promises.push(this.driverInfoDbDao.save(thirdParty.driverInformation));
        }
        if (thirdParty.mainAddress) {
            promises.push(this.addressDbDao.save(thirdParty.mainAddress));
        }
        if (thirdParty.mail) {
            promises.push(this.contactDetailsDbDao.save(thirdParty.mail));
        }
        if (thirdParty.mobile) {
            promises.push(this.contactDetailsDbDao.save(thirdParty.mobile));
        }
        if (thirdParty.phone) {
            promises.push(this.contactDetailsDbDao.save(thirdParty.phone));
        }
        return Promise.all(promises)
            .then(() => {
                const query = `INSERT OR REPLACE INTO ${ ThirdParty.TABLENAME } (id, civility, firstName, lastName, isActive, mainAddress, driverInformation, mail, mobile, phone, code, company)
VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`;
                const params = [
                    thirdParty.id,
                    JSON.stringify(thirdParty.civility),
                    thirdParty.firstName,
                    thirdParty.lastName,
                    thirdParty.isActive,
                    thirdParty.mainAddress ? thirdParty.mainAddress.id : null,
                    thirdParty.driverInformation ? thirdParty.driverInformation.id : null,
                    thirdParty.mail ? thirdParty.mail.id : null,
                    thirdParty.mobile ? thirdParty.mobile.id : null,
                    thirdParty.phone ? thirdParty.phone.id : null,
                    thirdParty.code,
                    thirdParty.company ? thirdParty.company.id : null,
                ];
                return this.sqlProvider.query(query, params)
                    .then(() => {
                        return thirdParty;
                    })
                    .catch(reason => {
                        this.logSqlError(reason);
                        return null;
                    });
            });
    }
}
